import { useCallback } from "react";

import { RouterContext } from "@/router";
import { create } from "@bufbuild/protobuf";
import { styled } from "@linaria/react";
import { createRootRouteWithContext, Outlet, useNavigate, useSearch } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import Drawer from "@gx/dls/drawer/Drawer";
import { OverlayProvider } from "@gx/dls/overlay/OverlayProvider";
import { withTheme } from "@gx/dls/theme/GalaxyTheme";
import type { PropsWithTheme } from "@gx/dls/theme/types";
import { GetArtifactRequestSchema } from "@gx/ts-contracts/services/ontology/api/artifact_pb";
import { GetSourceArtifactRequestSchema } from "@gx/ts-contracts/services/source/api/source_artifact_pb";
import { GetSourceRequestSchema } from "@gx/ts-contracts/services/source/api/source_pb";

import SmallScreenWrapper from "@/layouts/SmallScreenWrapper";

import ProjectArtifactContentViewerDrawer from "@/pages/projects/components/project/settings/artifacts/components/ProjectArtifactContentViewerDrawer";
import { useArtifactContent } from "@/pages/projects/components/project/settings/artifacts/hooks";
import CreateProjectModal from "@/pages/projects/forms/create/components/CreateProjectModal";
import CreateSourceModal from "@/pages/sources/components/create/CreateSourceModal";
import EditSourceDrawer from "@/pages/sources/components/edit/EditSourceDrawer";
import SourceArtifactContentViewerDrawer from "@/pages/sources/components/edit/tabs/artifacts/components/SourceArtifactContentViewerDrawer";
import { useSourceArtifactContent } from "@/pages/sources/components/edit/tabs/artifacts/hooks";
import { EditSourceDrawerTab } from "@/pages/sources/components/edit/types";
import SourcesModal from "@/pages/sources/SourcesModal";

import { ToastProvider } from "@/providers/toast/ToastProvider";

import { useGetArtifactQuery } from "@/api/queries/ontology";
import { useGetSourceArtifactQuery } from "@/api/queries/source-artifacts";
import { useGetSourceQuery } from "@/api/queries/sources";

const rootSearchSchema = z.object({
  projectArtifactId: z.string().optional(),
  sourceArtifactId: z.string().optional(),
  sources: z.coerce.boolean().optional(),
  createSource: z.coerce.boolean().optional(),
  createSourceType: z.string().optional(),
  createProject: z.coerce.boolean().optional(),
  createWorkspaceInvite: z.coerce.boolean().optional(),
  sourceId: z.string().optional(),
  sourceTab: z.enum(EditSourceDrawerTab).catch(EditSourceDrawerTab.OVERVIEW).optional(),
});

export const Route = createRootRouteWithContext<RouterContext>()({
  validateSearch: zodValidator(rootSearchSchema),
  component: RootComponent,
});

const RootComponentWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;

  height: 100vh;
  width: 100vw;
  background-color: ${({ theme }) => theme.color.background.base};

  ::selection {
    background-color: ${({ theme }) => theme.color.background.blue};
  }
`);

function RootComponent() {
  const { projectArtifactId, sourceArtifactId, sourceId, sourceTab } = useSearch({
    from: "__root__",
  });
  const navigate = useNavigate();

  const { data: sourceData } = useGetSourceQuery({
    input: create(GetSourceRequestSchema, { sourceId }),
    options: { enabled: !!sourceId },
  });

  const { data: projectArtifactData } = useGetArtifactQuery({
    input: create(GetArtifactRequestSchema, { artifactId: projectArtifactId }),
    options: { enabled: !!projectArtifactId },
  });

  const { data: sourceArtifactData } = useGetSourceArtifactQuery({
    input: create(GetSourceArtifactRequestSchema, { artifactId: sourceArtifactId }),
    options: { enabled: !!sourceArtifactId },
  });

  const projectArtifactContentState = useArtifactContent(
    projectArtifactData?.artifact?.objectStorageUri,
  );

  const sourceArtifactContentState = useSourceArtifactContent(sourceArtifactId);

  const handleCloseCreateSourceModal = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, createSource: undefined, createSourceType: undefined }),
    });
  }, [navigate]);

  const handleCloseSourceDrawer = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, sourceId: undefined, sourceTab: undefined }),
    });
  }, [navigate]);

  const handleCloseProjectArtifactDrawer = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, projectArtifactId: undefined }),
    });
  }, [navigate]);

  const handleCloseSourceArtifactDrawer = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, sourceArtifactId: undefined }),
    });
  }, [navigate]);

  const handleCloseSourcesModal = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, sources: undefined }),
    });
  }, [navigate]);

  const handleCloseCreateProjectModal = useCallback(() => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, createProject: undefined }),
    });
  }, [navigate]);

  return (
    <ToastProvider>
      <OverlayProvider>
        <RootComponentWrapper>
          <SmallScreenWrapper>
            <Outlet />
          </SmallScreenWrapper>
        </RootComponentWrapper>
        <CreateProjectModal onClose={handleCloseCreateProjectModal} />
        <CreateSourceModal onClose={handleCloseCreateSourceModal} />
        <SourcesModal onClose={handleCloseSourcesModal} />
        {sourceData?.source && (
          <Drawer
            open={!!(sourceId && sourceData?.source)}
            onClose={handleCloseSourceDrawer}
            width={720}
          >
            <EditSourceDrawer
              source={sourceData?.source}
              onClose={handleCloseSourceDrawer}
              initialTab={sourceTab}
            />
          </Drawer>
        )}
        {projectArtifactData?.artifact && (
          <Drawer
            open={!!projectArtifactData?.artifact}
            onClose={handleCloseProjectArtifactDrawer}
            width="60%"
          >
            <ProjectArtifactContentViewerDrawer
              artifact={projectArtifactData?.artifact}
              content={projectArtifactContentState.content}
              isLoading={projectArtifactContentState.isLoading}
              error={projectArtifactContentState.error}
              downloadUrl={projectArtifactContentState.downloadUrl}
              onClose={handleCloseProjectArtifactDrawer}
            />
          </Drawer>
        )}
        {sourceArtifactData?.artifact && (
          <Drawer
            open={!!sourceArtifactData?.artifact}
            onClose={handleCloseSourceArtifactDrawer}
            width="60%"
          >
            <SourceArtifactContentViewerDrawer
              artifact={sourceArtifactData.artifact}
              content={sourceArtifactContentState.content}
              isLoading={sourceArtifactContentState.isLoading}
              error={sourceArtifactContentState.error}
              downloadUrl={sourceArtifactContentState.downloadUrl}
              onClose={handleCloseSourceArtifactDrawer}
            />
          </Drawer>
        )}
      </OverlayProvider>
    </ToastProvider>
  );
}
